import { Headphones, Pause, Play, X } from "@phosphor-icons/react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef } from "react";
import type { MediaPlaybackRequest } from "./media-player-types";

type DockRegistrar = (element: HTMLDivElement | null) => void;

const MediaPlayerSidebarDockContext = createContext<DockRegistrar | null>(null);

export function MediaPlayerSidebarDockProvider({
  register,
  children,
}: {
  register: DockRegistrar;
  children: ReactNode;
}) {
  return (
    <MediaPlayerSidebarDockContext.Provider value={register}>
      {children}
    </MediaPlayerSidebarDockContext.Provider>
  );
}

/** A persistent portal target rendered inside the desktop side navigation. */
export function MediaPlayerSidebarDock() {
  const register = useContext(MediaPlayerSidebarDockContext);
  const elementRef = useRef<HTMLDivElement | null>(null);

  const setElement = useCallback((element: HTMLDivElement | null) => {
    elementRef.current = element;
  }, []);

  useEffect(() => {
    register?.(elementRef.current);
    return () => register?.(null);
  }, [register]);

  return <div ref={setElement} className="hidden w-full md:block" />;
}

export function SidebarMediaPlayer({
  session,
  playing,
  currentTime,
  duration,
  onExpand,
  onTogglePlayback,
  onClose,
}: {
  session: MediaPlaybackRequest;
  playing: boolean;
  currentTime: number;
  duration: number;
  onExpand: () => void;
  onTogglePlayback: () => void;
  onClose: () => void;
}) {
  const progress = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;
  const imageUrl = session.mediaType === "audio" ? session.imageUrl : null;
  const mediaLabel = session.mediaType === "audio" ? "Podcast" : session.kind;

  return (
    <section
      aria-label={`Collapsed mini player: ${session.title}`}
      className="media-sidebar-player w-full overflow-hidden rounded-xl border border-border-hairline bg-surface-elevated shadow-card"
    >
      <button
        type="button"
        aria-label={`Expand mini player: ${session.title}`}
        title={session.title}
        onClick={onExpand}
        className="grid h-14 w-full place-items-center text-ink-brand transition-colors hover:bg-surface-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus 2xl:hidden"
      >
        <span className="relative grid h-8 w-8 place-items-center rounded-lg bg-surface-secondary">
          {session.mediaType === "audio" ? (
            <Headphones size={18} weight="duotone" aria-hidden="true" />
          ) : (
            <Play size={18} weight="fill" aria-hidden="true" />
          )}
          {playing ? (
            <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border-2 border-surface-elevated bg-ink-brand" />
          ) : null}
        </span>
      </button>

      <div className="hidden min-w-0 items-center gap-2.5 p-2.5 2xl:flex">
        <button
          type="button"
          aria-label={`Expand mini player: ${session.title}`}
          onClick={onExpand}
          className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-lg bg-surface-secondary text-ink-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        >
          {imageUrl ? (
            <img src={imageUrl} alt="" className="h-full w-full object-cover" />
          ) : session.mediaType === "audio" ? (
            <Headphones size={18} weight="duotone" aria-hidden="true" />
          ) : (
            <Play size={17} weight="fill" aria-hidden="true" />
          )}
        </button>
        <button
          type="button"
          aria-label={`Expand mini player: ${session.title}`}
          onClick={onExpand}
          className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        >
          <p className="m-0 truncate font-sans text-xs font-medium text-ink-heading">
            {session.title}
          </p>
          <p className="m-0 mt-0.5 font-mono text-2xs tabular-nums text-ink-tertiary">
            {playing ? "Playing" : "Paused"} · {formatPlaybackTime(currentTime)}
          </p>
        </button>
        <button
          type="button"
          aria-label={playing ? "Pause" : "Play"}
          onClick={onTogglePlayback}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-surface-secondary text-ink-brand transition-colors hover:bg-teal-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        >
          {playing ? (
            <Pause size={15} weight="fill" aria-hidden="true" />
          ) : (
            <Play size={15} weight="fill" aria-hidden="true" />
          )}
        </button>
        <button
          type="button"
          aria-label="Close player"
          onClick={onClose}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-ink-tertiary transition-colors hover:bg-surface-tertiary hover:text-ink-heading focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        >
          <X size={14} weight="bold" aria-hidden="true" />
        </button>
      </div>
      <div className="hidden h-1 bg-surface-track 2xl:block" aria-hidden="true">
        <div className="h-full bg-ink-brand" style={{ width: `${progress}%` }} />
      </div>
      <span className="sr-only">
        {mediaLabel}, {playing ? "playing" : "paused"}, {formatPlaybackTime(currentTime)}
      </span>
    </section>
  );
}

function formatPlaybackTime(value: number): string {
  if (!Number.isFinite(value) || value < 0) return "0:00";
  const total = Math.floor(value);
  return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, "0")}`;
}
